import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  approveSample,
  deleteSample as deleteSampleRequest,
  getSample,
  getSamples,
  unapproveSample,
} from '../services/samples_service'
import './Samples.css'

const formatDate = (date) =>
  date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

const getValue = (sample, keys, fallback = '-') =>
  keys
    .map((key) => sample?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const formatApiDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : formatDate(date)
}
const getRequesterName = (requestUser) => {
  if (!requestUser) return '-'
  if (typeof requestUser !== 'object') return requestUser

  const name = getValue(
    requestUser,
    ['name', 'full_name', 'user_fullname', 'fullName', 'fullname'],
    '',
  )
  if (name) return name

  const firstName = getValue(requestUser, ['first_name', 'firstName'], '')
  const lastName = getValue(requestUser, ['last_name', 'lastName'], '')
  return [firstName, lastName].filter(Boolean).join(' ') || '-'
}
const getSampleBooks = (sample) => {
  const books =
    sample?.books ||
    sample?.sample_books ||
    sample?.sampleBooks ||
    sample?.requested_books ||
    sample?.requestedBooks ||
    sample?.bookList ||
    []
  return (Array.isArray(books) ? books : []).map((book, index) => ({
    id: getValue(book, ['bookid', 'bookId', 'book_id', 'id'], index + 1),
    name: getValue(
      book,
      ['name', 'bookname', 'bookName', 'book_title', 'book_name', 'title'],
      'Unnamed book',
    ),
    quantity: getValue(book, ['quantity', 'qty', 'book_quantity'], 0),
  }))
}
const getSampleRecords = (sample, keys) => {
  const records = keys
    .map((key) => sample?.[key])
    .find((value) => Array.isArray(value))
  return Array.isArray(records) ? records : []
}
const getBookRecords = (records) =>
  records.map((book, index) => ({
    id: getValue(book, ['bookid', 'bookId', 'book_id', 'id'], index + 1),
    name: getValue(
      book,
      ['name', 'bookname', 'bookName', 'book_title', 'book_name', 'title'],
      'Unnamed book',
    ),
    quantity: getValue(book, ['quantity', 'qty', 'book_quantity'], 0),
    good: getValue(book, ['good', 'good_quantity', 'goodQuantity'], 0),
    bad: getValue(book, ['bad', 'bad_quantity', 'badQuantity'], 0),
    kept: getValue(book, ['kept', 'kept_quantity', 'keptQuantity'], 0),
  }))
const toSampleRow = (sample) => ({
  ...sample,
  id: getValue(sample, ['sampleid', 'sampleId', 'id']),
  requestedBy: getRequesterName(sample.request_user),
  requested: formatApiDate(
    getValue(sample, [
      'requested',
      'requested_at',
      'request_date',
      'created_at',
    ]),
  ),
  approved: formatApiDate(
    getValue(sample, ['approve_date', 'approved_at', 'approval_date']),
  ),
  released: formatApiDate(
    getValue(sample, ['released', 'released_at', 'release_date']),
  ),
  status: getValue(sample, ['status', 'sample_status'], 'Requested'),
  books: getSampleBooks(sample),
  deliveredBooks: (() => {
    const deliveredRecords = getSampleRecords(sample, [
      'delivered',
      'delivered_books',
      'deliveredBooks',
      'DeliveredBooks',
      'delivery',
      'delivery_books',
    ])
    return getBookRecords(
      deliveredRecords.length ? deliveredRecords : getSampleBooks(sample),
    )
  })(),
  returnedBooks: getBookRecords(
    getSampleRecords(sample, [
      'returns',
      'returned_books',
      'returnedBooks',
      'return_books',
      'sample_returns',
    ]),
  ),
})

function Samples() {
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const canAddSample = hasPermission('addSample')
  const canApproveSample = hasPermission('approveSample')
  const canUnapproveSample = hasPermission('unapproveSample')
  const canDeleteSample = hasPermission('deleteSample')
  const [samples, setSamples] = useState([])
  const [selectedSample, setSelectedSample] = useState(null)
  const [selectedSampleTab, setSelectedSampleTab] = useState('delivered')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [loadingSampleId, setLoadingSampleId] = useState(null)
  const [pendingDeleteSampleId, setPendingDeleteSampleId] = useState(null)
  const [pendingApprovalSample, setPendingApprovalSample] = useState(null)
  const [pendingUnapprovalSample, setPendingUnapprovalSample] = useState(null)
  const [approvalComment, setApprovalComment] = useState('')

  const loadSamples = async (cursor = null, page = 1) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getSamples({ cursor, page })
      setSamples(result.samples.map(toSampleRow))
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSamples()
  }, [])

  const confirmDeleteSample = async () => {
    if (pendingDeleteSampleId === null) return

    const sampleId = pendingDeleteSampleId
    setLoadingSampleId(sampleId)
    setError('')
    try {
      await deleteSampleRequest(sampleId)
      setPendingDeleteSampleId(null)
      await loadSamples(currentCursor, currentPage)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoadingSampleId(null)
    }
  }

  const viewSample = async (sampleId) => {
    setLoadingSampleId(sampleId)
    setError('')
    try {
      setSelectedSampleTab('delivered')
      setSelectedSample(toSampleRow(await getSample(sampleId)))
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoadingSampleId(null)
    }
  }

  const submitApproval = async (event) => {
    event.preventDefault()
    if (!pendingApprovalSample) return

    const sample = pendingApprovalSample
    setLoadingSampleId(sample.id)
    setError('')
    try {
      await approveSample(sample.id, {
        userid: user?.userid ?? user?.user_id ?? user?.id,
        comment: approvalComment.trim(),
        status: 'approved',
      })
      setPendingApprovalSample(null)
      setApprovalComment('')
      await loadSamples(currentCursor, currentPage)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoadingSampleId(null)
    }
  }

  const confirmUnapproval = async () => {
    if (!pendingUnapprovalSample) return

    const sample = pendingUnapprovalSample
    setLoadingSampleId(sample.id)
    setError('')
    try {
      await unapproveSample(sample.id, 'requested')
      setPendingUnapprovalSample(null)
      await loadSamples(currentCursor, currentPage)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoadingSampleId(null)
    }
  }

  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadSamples(nextCursor, nextPage || currentPage)
  }

  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadSamples(previous.cursor, previous.page)
  }

  return (
    <div className='samples-page'>
      <nav className='samples-page__breadcrumb' aria-label='Breadcrumb'>
        <span>Dashboard</span>
        <span aria-hidden='true'>/</span>
        <strong>Samples</strong>
      </nav>
      <header className='samples-page__header'>
        <div>
          <span className='app-content__eyebrow'>Distribution</span>
          <h1>Samples</h1>
          <p>Track sample requests from submission through release.</p>
        </div>
        {canAddSample && (
          <button
            className='samples-page__request-button'
            type='button'
            onClick={() => navigate('/samples/new')}
          >
            <span aria-hidden='true'>+</span> Request new sample
          </button>
        )}
      </header>
      {error && (
        <div className='samples-page__error' role='alert'>
          {error}
          <button
            type='button'
            onClick={() => loadSamples(currentCursor, currentPage)}
          >
            Retry
          </button>
        </div>
      )}

      <section
        className='samples-table-card'
        aria-labelledby='samples-list-title'
      >
        <div className='samples-table-card__header'>
          <div>
            <h2 id='samples-list-title'>Requested samples</h2>
            <p>{samples.length} sample requests</p>
          </div>
        </div>
        <div className='samples-table-wrap'>
          <table className='samples-table'>
            <thead>
              <tr>
                <th>ID</th>
                <th>Requested by</th>
                <th>Requested</th>
                <th>Approved</th>
                <th>Released</th>
                <th>Status</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='samples-table__empty' colSpan='7'>
                    Loading samples...
                  </td>
                </tr>
              ) : samples.length ? (
                samples.map((sample) => (
                  <tr key={sample.id}>
                    <td>
                      <strong>{sample.id}</strong>
                    </td>
                    <td>{sample.requestedBy}</td>
                    <td>{sample.requested}</td>
                    <td>{sample.approved}</td>
                    <td>{sample.released}</td>
                    <td>
                      <span
                        className={`samples-status samples-status--${sample.status.toLowerCase()}`}
                      >
                        {sample.status}
                      </span>
                    </td>
                    <td>
                      <div className='samples-actions'>
                        <button
                          type='button'
                          onClick={() => viewSample(sample.id)}
                          disabled={loadingSampleId === sample.id}
                        >
                          {loadingSampleId === sample.id
                            ? 'Loading...'
                            : 'View'}
                        </button>
                        {canApproveSample &&
                          ['requested', 'unapproved'].includes(
                            sample.status.toLowerCase(),
                          ) && (
                            <button
                              type='button'
                              onClick={() => {
                                setPendingApprovalSample(sample)
                                setApprovalComment('')
                              }}
                              disabled={loadingSampleId === sample.id}
                            >
                              Approve
                            </button>
                          )}
                        {canUnapproveSample &&
                          sample.status.toLowerCase() === 'approved' && (
                            <button
                              type='button'
                              onClick={() => setPendingUnapprovalSample(sample)}
                              disabled={loadingSampleId === sample.id}
                            >
                              Unapprove
                            </button>
                          )}
                        {canDeleteSample &&
                          ['requested', 'unapproved'].includes(
                            sample.status.toLowerCase(),
                          ) && (
                            <button
                              type='button'
                              className='samples-actions__delete'
                              onClick={() =>
                                setPendingDeleteSampleId(sample.id)
                              }
                              disabled={loadingSampleId === sample.id}
                            >
                              {loadingSampleId === sample.id
                                ? 'Deleting...'
                                : 'Delete'}
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='samples-table__empty' colSpan='7'>
                    No sample requests yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='samples-pagination'>
          <span>Showing up to 20 sample requests</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {selectedSample && (
        <div
          className='samples-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && setSelectedSample(null)
          }
        >
          <section
            className='samples-modal samples-detail-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='sample-details-title'
          >
            <div className='samples-detail-modal__topbar'>
              <div>
                <span className='app-content__eyebrow'>
                  Distribution record
                </span>
                <h2 id='sample-details-title'>{selectedSample.id}</h2>
              </div>
              <button
                type='button'
                onClick={() => setSelectedSample(null)}
                aria-label='Close sample details'
              >
                x
              </button>
            </div>
            <div className='samples-detail-modal__body'>
              <aside className='samples-detail-summary'>
                <span className='samples-detail-summary__label'>
                  Request status
                </span>
                <span
                  className={`samples-status samples-status--${selectedSample.status.toLowerCase()}`}
                >
                  {selectedSample.status}
                </span>
                <div className='samples-detail-summary__metric'>
                  <strong>{Number(selectedSample.books_requested)}</strong>
                  <span>Total books</span>
                </div>
                <dl className='samples-detail-facts'>
                  <div>
                    <dt>Requested by</dt>
                    <dd>{selectedSample.requestedBy}</dd>
                  </div>
                  <div>
                    <dt>Requested</dt>
                    <dd>{selectedSample.requested}</dd>
                  </div>
                  <div>
                    <dt>Approved</dt>
                    <dd>{selectedSample.approved}</dd>
                  </div>
                  <div>
                    <dt>Released</dt>
                    <dd>{selectedSample.released}</dd>
                  </div>
                </dl>
              </aside>
              <section className='samples-detail-books'>
                <div className='samples-detail-books__tabs' role='tablist'>
                  <button
                    className={
                      selectedSampleTab === 'delivered' ? 'is-active' : ''
                    }
                    type='button'
                    role='tab'
                    aria-selected={selectedSampleTab === 'delivered'}
                    onClick={() => setSelectedSampleTab('delivered')}
                  >
                    Delivered
                  </button>
                  <button
                    className={
                      selectedSampleTab === 'returns' ? 'is-active' : ''
                    }
                    type='button'
                    role='tab'
                    aria-selected={selectedSampleTab === 'returns'}
                    onClick={() => setSelectedSampleTab('returns')}
                  >
                    Returns
                  </button>
                </div>
                {selectedSampleTab === 'delivered' ? (
                  <div className='samples-detail-books__panel' role='tabpanel'>
                    <div className='samples-detail-books__heading'>
                      <div>
                        <span className='app-content__eyebrow'>
                          Distribution record
                        </span>
                        <h3>Delivered books</h3>
                      </div>
                      <span>{selectedSample.deliveredBooks.length} titles</span>
                    </div>
                    {selectedSample.deliveredBooks.length ? (
                      <div className='samples-detail-books__table-wrap'>
                        <table className='samples-detail-books__table'>
                          <thead>
                            <tr>
                              <th>S/N</th>
                              <th>Book</th>
                              <th>Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSample.deliveredBooks.map(
                              (book, index) => (
                                <tr key={`${book.id}-${index}`}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <strong>{book.name}</strong>
                                  </td>
                                  <td>{book.quantity}</td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className='samples-detail-books__empty'>
                        No delivered books were recorded for this request.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className='samples-detail-books__panel' role='tabpanel'>
                    <div className='samples-detail-books__heading'>
                      <div>
                        <span className='app-content__eyebrow'>
                          Distribution record
                        </span>
                        <h3>Returned books</h3>
                      </div>
                      <span>{selectedSample.returnedBooks.length} titles</span>
                    </div>
                    {selectedSample.returnedBooks.length ? (
                      <div className='samples-detail-books__table-wrap'>
                        <table className='samples-detail-books__table samples-detail-books__table--returns'>
                          <thead>
                            <tr>
                              <th>S/N</th>
                              <th>Book</th>
                              <th>Qty</th>
                              <th>Good</th>
                              <th>Bad</th>
                              <th>Kept</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSample.returnedBooks.map((book, index) => (
                              <tr key={`${book.id}-${index}`}>
                                <td>{index + 1}</td>
                                <td>
                                  <strong>{book.name}</strong>
                                </td>
                                <td>{book.quantity}</td>
                                <td>{book.good}</td>
                                <td>{book.bad}</td>
                                <td>{book.kept}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className='samples-detail-books__empty'>
                        No returned books were recorded for this request.
                      </p>
                    )}
                  </div>
                )}
              </section>
            </div>
          </section>
        </div>
      )}

      {pendingDeleteSampleId && (
        <div
          className='samples-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            setPendingDeleteSampleId(null)
          }
        >
          <section
            className='samples-modal samples-confirmation-modal'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='delete-sample-title'
            aria-describedby='delete-sample-description'
          >
            <div className='samples-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Delete request</span>
                <h2 id='delete-sample-title'>Delete sample?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingDeleteSampleId(null)}
                aria-label='Close delete confirmation'
              >
                x
              </button>
            </div>
            <p
              className='samples-confirmation-modal__description'
              id='delete-sample-description'
            >
              This sample request will be permanently deleted. This action
              cannot be undone.
            </p>
            <div className='samples-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingDeleteSampleId(null)}
                disabled={loadingSampleId === pendingDeleteSampleId}
              >
                Cancel
              </button>
              <button
                className='samples-modal__delete-button'
                type='button'
                onClick={confirmDeleteSample}
                disabled={loadingSampleId === pendingDeleteSampleId}
              >
                {loadingSampleId === pendingDeleteSampleId
                  ? 'Deleting...'
                  : 'Delete sample'}
              </button>
            </div>
          </section>
        </div>
      )}

      {pendingApprovalSample && (
        <div
          className='samples-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            loadingSampleId !== pendingApprovalSample.id &&
            setPendingApprovalSample(null)
          }
        >
          <section
            className='samples-modal samples-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='approve-sample-title'
          >
            <div className='samples-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Approve request</span>
                <h2 id='approve-sample-title'>Approve sample</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingApprovalSample(null)}
                disabled={loadingSampleId === pendingApprovalSample.id}
                aria-label='Close approval form'
              >
                x
              </button>
            </div>
            <form onSubmit={submitApproval}>
              <p className='samples-approval-modal__description'>
                Add an approval comment for sample {pendingApprovalSample.id}.
              </p>
              <label>
                Approval comment
                <textarea
                  rows='4'
                  value={approvalComment}
                  onChange={(event) => setApprovalComment(event.target.value)}
                  required
                  autoFocus
                />
              </label>
              <div className='samples-modal__actions'>
                <button
                  type='button'
                  onClick={() => setPendingApprovalSample(null)}
                  disabled={loadingSampleId === pendingApprovalSample.id}
                >
                  Cancel
                </button>
                <button
                  className='samples-modal__approve-button'
                  type='submit'
                  disabled={loadingSampleId === pendingApprovalSample.id}
                >
                  {loadingSampleId === pendingApprovalSample.id
                    ? 'Approving...'
                    : 'Approve sample'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {pendingUnapprovalSample && (
        <div
          className='samples-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            loadingSampleId !== pendingUnapprovalSample.id &&
            setPendingUnapprovalSample(null)
          }
        >
          <section
            className='samples-modal samples-confirmation-modal'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='unapprove-sample-title'
            aria-describedby='unapprove-sample-description'
          >
            <div className='samples-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Approval status</span>
                <h2 id='unapprove-sample-title'>Unapprove sample?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingUnapprovalSample(null)}
                disabled={loadingSampleId === pendingUnapprovalSample.id}
                aria-label='Close unapproval confirmation'
              >
                x
              </button>
            </div>
            <p
              className='samples-confirmation-modal__description'
              id='unapprove-sample-description'
            >
              Sample {pendingUnapprovalSample.id} will return to the unapproved
              status.
            </p>
            <div className='samples-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingUnapprovalSample(null)}
                disabled={loadingSampleId === pendingUnapprovalSample.id}
              >
                Cancel
              </button>
              <button
                className='samples-modal__delete-button'
                type='button'
                onClick={confirmUnapproval}
                disabled={loadingSampleId === pendingUnapprovalSample.id}
              >
                {loadingSampleId === pendingUnapprovalSample.id
                  ? 'Unapproving...'
                  : 'Unapprove sample'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default Samples
